import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { switchMap, map, catchError, take, finalize } from 'rxjs/operators';
import * as ExcelManagementActions from './excel-management.actions';
import { TExcelManagementActionType } from './excel-management.models';
import { FactoryFacade } from '../factory/factory.facade';
import { TDOWNLOAD_LISTING_DATA_PAYLOAD, TExcelContext } from '@monorepo/types';
import { ListingHelperClass } from '../../helpers/listing-helper';
import {
  UploadDownLoadFileService,
  MmEnvironmentService,
  MachineMaintenanceDataService,
  AppNotificationUtilClass,
  DownloadFileSystemAppService,
  CapacitorHttpService,
} from '@monorepo/services';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { ExcelUtilClass } from '@monorepo/utils';
import { MachineListFacade } from '../machine-list/machine-list.facade';
import { EmployeeManagementFacade } from '../employee-management/employee-management.facade';
import { LocationManagementFacade } from '../location-management/location-management.facade';
import { BreakdownReasonsFacade } from '../breakdown-reasons/breakdown-reasons.facade';
import { SparePartListFacade } from '../sparepart-list/sparepart-list.facade';
import { NeedleManagementFacade } from '../needle-management/needle-management.facade';
import { NptFacade } from '../npt/npt.facade';
import { AttachmentFacade } from '../attachment/attachment.facade';
import { QcoFacade } from '../qco/qco.facade';

function _getDownloadFileName(excelContext: TExcelContext) {
  switch (excelContext) {
    case 'MACHINE':
      return 'machine-list-template.xlsx';
    case 'LOCATION':
      return 'location-management-template.xlsx';
    case 'EMPLOYEE':
      return 'employee-management-template.xlsx';
    case 'BREAKDOWN_REASON':
      return 'breakdown-reasons-template.xlsx';
    case 'SPAREPART':
      return 'sparepart-template.xlsx';
    case 'NEEDLE':
      return 'needle-list-template.xlsx';
    case 'NEEDLE_REQUEST_APPROVAL':
      return 'needle-requests-template.xlsx';
    case 'NPT_REASON':
      return 'npt-reasons-template.xlsx';
    default:
      return 'list-download-template.xlsx';
  }
}

function _getDownloadErrorFileName(excelContext: TExcelContext) {
  switch (excelContext) {
    case 'MACHINE':
      return 'machine-list-error-template.xlsx';
    case 'LOCATION':
      return 'location-management-error-template.xlsx';
    case 'EMPLOYEE':
      return 'employee-management-error-template.xlsx';
    case 'BREAKDOWN_REASON':
      return 'breakdown-reasons-error-template.xlsx';
    case 'SPAREPART':
      return 'sparepart-list-error-template.xlsx';
    case 'SPAREPART_REQUEST_APPROVAL':
      return 'sparepart-request-list-template.xlsx';
    case 'NEEDLE':
      return 'needle-list-template.xlsx';
    case 'NEEDLE_REQUEST_APPROVAL':
      return 'needle-requests-template.xlsx';
    case 'NPT_REASON':
      return 'npt-reasons-template.xlsx';
    case 'ATTACHMENT':
      return 'attachments-error-template.xlsx';
    default:
      return 'error-template.xlsx';
  }
}

function _getDownloadListingDataFileName(excelContext: TExcelContext) {
  switch (excelContext) {
    case 'MACHINE':
      return 'machine-list-data.xlsx';
    case 'LOCATION':
      return 'location-management-data.xlsx';
    case 'EMPLOYEE':
      return 'employee-management-data.xlsx';
    case 'BREAKDOWN_REASON':
      return 'breakdown-reasons-data.xlsx';
    case 'SPAREPART':
      return 'sparepart-list-data.xlsx';
    case 'SPAREPART_REQUEST_APPROVAL':
      return 'sparepart-request-list-data.xlsx';
    case 'NEEDLE':
      return 'needle-list-data.xlsx';
    case 'NEEDLE_REQUEST_APPROVAL':
      return 'needle-requests-data.xlsx';
    case 'NPT_REASON':
      return 'npt-reasons-data.xlsx';
    case 'ATTACHMENT':
      return 'attachments.xlsx';
    case 'LINE_SETUP_TICKET':
      return 'line-setup-tickets-data.xlsx';
    case 'LINE_OVERVIEW':
      return 'line-overview-data.xlsx';
  }
}

function _getUploadFormData(file: File, excelContext: TExcelContext, factoryId: string) {
  const uploadFormData = new FormData();
  uploadFormData.append('file', file, file.name);
  uploadFormData.append('factoryId', factoryId);
  uploadFormData.append('excelContext', excelContext);
  return uploadFormData;
}

@Injectable({ providedIn: 'root' })
export class ExcelManagementEffects {
  constructor(
    private actions$: Actions,
    private fileService: UploadDownLoadFileService,
    private factoryFacade: FactoryFacade,
    private machineListFacade: MachineListFacade,
    private employeeManagementFacade: EmployeeManagementFacade,
    private locationManagementFacade: LocationManagementFacade,
    private breakdownReasonsFacade: BreakdownReasonsFacade,
    private sparePartListFacade: SparePartListFacade,
    private needleManagementFacade: NeedleManagementFacade,
    private APIHelper: AppNotificationUtilClass,
    private listingHelper: ListingHelperClass,
    private excelHelper: ExcelUtilClass,
    private mmEnvironmentService: MmEnvironmentService,
    private mmDataService: MachineMaintenanceDataService,
    private nptFacade: NptFacade,
    private attachmentFacade: AttachmentFacade,
    private qcoFacade: QcoFacade,
    private downloadFileSystemAppService: DownloadFileSystemAppService,
    private capacitorHttpService: CapacitorHttpService,
  ) {}

  /**
   * On native, `CapacitorHttp` plugin patches XHR/fetch and corrupts binary blob responses.
   * Uses `CapacitorHttp` API directly to get clean base64 data, then write to filesystem.
   */
  async #nativeDownloadXlsx(url: string, payload: TDOWNLOAD_LISTING_DATA_PAYLOAD | null, fileName: string) {
    const deviceHeaders = await this.capacitorHttpService.getDeviceHeader();
    const response = await CapacitorHttp.request({
      method: 'POST',
      url,
      headers: { 'Content-Type': 'application/json', ...deviceHeaders },
      data: payload,
      responseType: 'blob',
    });
    if (response.status !== 200) {
      throw new Error(`[ERROR](excel-management.effects): HTTP ${response.status}`);
    }
    this.downloadFileSystemAppService.downloadFilesInApp(fileName.replace('.xlsx', ''), response.data, 'xlsx');
  }

  #executeDownload<S, F>(
    loaderEl: HTMLIonLoadingElement,
    fileName: string,
    nativeUrl: string,
    nativePayload: TDOWNLOAD_LISTING_DATA_PAYLOAD | null,
    webDownload$: Observable<Blob>,
    successAction: () => S,
    failureAction: () => F,
  ): Observable<S | F> {
    if (Capacitor.isNativePlatform()) {
      return from(this.#nativeDownloadXlsx(nativeUrl, nativePayload, fileName)).pipe(
        finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
        map(() => successAction()),
        catchError((): Observable<S | F> => of(failureAction())),
      );
    }
    return webDownload$.pipe(
      finalize(() => this.APIHelper.dismissLoadingV2(loaderEl)),
      map((res) => {
        this.excelHelper.downloadFile(res, fileName);
        return successAction();
      }),
      catchError((): Observable<S | F> => of(failureAction())),
    );
  }

  uploadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.UploadExcel),
      switchMap((action: ExcelManagementActions.UploadExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [
              loaderEl,
              action,
              _getUploadFormData(action.payload.file, action.payload.excelContext, factoryId),
            ] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, uploadFormData]) =>
        this.fileService.uploadExcel(this.mmEnvironmentService.getUrlFromEnv('UPLOAD_FILE_URL'), uploadFormData).pipe(
          finalize(() => {
            this.APIHelper.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.listingHelper.openUploadSummaryModal(res, action.payload.excelContext);
            switch (action.payload.excelContext) {
              case 'MACHINE':
                this.machineListFacade.resetMachineListInfo('ACTION');
                break;
              case 'LOCATION':
                this.locationManagementFacade.resetLocationInfo('ACTION');
                break;
              case 'EMPLOYEE':
                this.employeeManagementFacade.resetEmployeeListInfo('ACTION');
                break;
              case 'BREAKDOWN_REASON':
                this.breakdownReasonsFacade.resetBreakdownReasonsListInfo('ACTION');
                break;
              case 'SPAREPART':
                this.sparePartListFacade.resetSparePartListInfo('ACTION');
                break;
              case 'NEEDLE':
                this.needleManagementFacade.resetNeedleListInfo('ACTION');
                break;
              case 'NPT_REASON':
                this.nptFacade.resetNptReasonsList();
                break;
              case 'ATTACHMENT':
                this.attachmentFacade.resetAttachmentListing();
                break;
              case 'LINE_SETUP_TICKET':
                this.qcoFacade.resetLineSetupTicketListing();
                break;
            }
            return new ExcelManagementActions.UploadExcelSuccess();
          }),
          catchError(() => {
            return of(new ExcelManagementActions.UploadExcelFailure());
          }),
        ),
      ),
    ),
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.DownloadExcel),
      switchMap((action: ExcelManagementActions.DownloadExcel) =>
        this.factoryFacade.getFactoryId().pipe(
          take(1),
          map((factoryId) => {
            return [action, factoryId] as const;
          }),
        ),
      ),
      switchMap((args) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, ...args] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action, factoryId]) => {
        const url = this.mmEnvironmentService
          .getUrlFromEnv('DOWNLOAD_SAMPLE_TEMPLATE_URL')
          .replace(':excelContext', action.excelContext)
          .replace(':factoryId', factoryId);
        const fileName = _getDownloadFileName(action.excelContext);
        return this.#executeDownload(
          loaderEl,
          fileName,
          url,
          null,
          this.fileService.downloadSampleTemplate(url),
          () => new ExcelManagementActions.DownloadExcelSuccess(),
          () => new ExcelManagementActions.DownloadExcelFailure(),
        );
      }),
    ),
  );

  downloadExcelErrorTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.DownloadErrorExcel),
      switchMap((action: ExcelManagementActions.DownloadErrorExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        const url = this.mmEnvironmentService
          .getUrlFromEnv('DOWNLOAD_ERROR_SHEET_URL')
          .replace('errorFileKey', action.payload.errorKey);
        const fileName = _getDownloadErrorFileName(action.payload.excelContext);
        return this.#executeDownload(
          loaderEl,
          fileName,
          url,
          null,
          this.fileService.downloadErrorTemplate(url),
          () => new ExcelManagementActions.DownloadExcelSuccess(),
          () => new ExcelManagementActions.DownloadErrorExcelFailure(),
        );
      }),
    ),
  );

  downloadListingDataExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TExcelManagementActionType.DownloadListingDataExcel),
      switchMap((action: ExcelManagementActions.DownloadListingDataExcel) =>
        from(this.APIHelper.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) => {
        const fileName = _getDownloadListingDataFileName(action.excelContext);
        const nativeUrl = this.mmEnvironmentService
          .getUrlFromEnv('DOWNLOAD_LISTING_DATA')
          .replace(':context', action.excelContext);
        return this.#executeDownload(
          loaderEl,
          fileName,
          nativeUrl,
          action.payload,
          this.mmDataService.downloadAllListingData(action.excelContext, action.payload),
          () => new ExcelManagementActions.DownloadListingDataExcelSuccess(),
          () => new ExcelManagementActions.DownloadListingDataExcelFailure(),
        );
      }),
    ),
  );
}
